import { useEffect, useState, useRef } from 'react';
import Slider from 'react-slick';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const Sponsors = ({ showTitle = true, variant = "default" }) => {
  const [isMobile, setIsMobile] = useState(false);
  const sliderRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const CustomPrevArrow = ({ onClick }) => (
    <button
      onClick={onClick}
      className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center text-primary-navy hover:bg-primary-navy hover:text-white transition-colors duration-300 focus:outline-none"
      aria-label="Previous slide"
    >
      <ChevronLeft className="w-6 h-6" />
    </button>
  );

  const CustomNextArrow = ({ onClick }) => (
    <button
      onClick={onClick}
      className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center text-primary-navy hover:bg-primary-navy hover:text-white transition-colors duration-300 focus:outline-none"
      aria-label="Next slide"
    >
      <ChevronRight className="w-6 h-6" />
    </button>
  );

  const sponsors = [
    {
      id: 1,
      name: "Minister of Transport and Logistics, Ethiopia",
      logo: "/assets/images/MoTL.jpg",
      website: "https://www.motl.gov.et/"
    },
    {
      id: 2,
      name: "Ethiopia Maritime Authority",
      logo: "/assets/images/ema.png",
      website: "https://etmaritime.com/"
    },
    {
      id: 3,
      name: "Ethiopian Airlines",
      logo: "/assets/images/airlines.png",
      website: "https://www.ethiopianairlines.com/"
    },
    {
      id: 4,
      name: "Ethiopian Maritime Training Institute",
      logo: "/assets/images/emtisc.jpg",
      website: "https://emticorp.com/"
    },
    {
      id: 5,
      name: "EMA Marine ",
      logo: "/assets/images/EMA_logo.png",
      website: "https://www.emacrew.com/"
    },
    {
      id: 6,
      name: "Skylight Hotel and Conference Venue",
      logo: "/assets/images/airliness.png",
      website: "https://www.ethiopianskylighthotel.com/"
    }
  ]

  const SponsorCard = ({ sponsor }) => {
    // Special case for Ethiopian Maritime Training Institute logo
    const isEthiopianMaritime = sponsor.id === 4;
    
    const handleLogoClick = (e, url) => {
      if (url && url !== '#') {
        e.preventDefault();
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    };
    
    return (
      <div className="px-4 py-8 h-full">
        <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105 border border-gray-100 group h-full flex flex-col">
          {/* Logo */}
          <div className="flex-1 flex items-center justify-center w-full mb-4">
            <div 
              className={`relative w-full h-32 flex items-center justify-center ${isEthiopianMaritime ? 'max-h-[120px]' : ''} cursor-pointer`}
              onClick={(e) => handleLogoClick(e, sponsor.website)}
              title={sponsor.website === '#' ? 'Website coming soon' : `Visit ${sponsor.name}`}
            >
              <img
                src={sponsor.logo}
                alt={`${sponsor.name} logo`}
                className={`max-h-full w-auto ${isEthiopianMaritime ? 'h-auto max-h-[100px]' : 'h-full max-h-[100px]'} transition-transform duration-300 group-hover:scale-105`}
                style={{
                  objectFit: 'contain',
                  objectPosition: 'center',
                  width: 'auto',
                  maxWidth: '100%',
                  cursor: sponsor.website === '#' ? 'default' : 'pointer'
                }}
                onError={(e) => {
                  e.target.src = "/placeholder-logo.png";
                }}
              />
            </div>
          </div>
          {/* Name */}
          <h3 className="text-center font-semibold text-primary-navy mt-4">
            {sponsor.name}
          </h3>
        </div>
      </div>
    );
  }

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: isMobile ? 1 : 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    arrows: false, // Disable default arrows
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
          arrows: true,
        }
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          arrows: true,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true
        }
      }
    ]
  };

  if (variant === "compact") {
    return (
      <div className="py-12 bg-gray-50">
        {showTitle && (
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-primary-navy mb-2">Our Sponsors</h2>
            <div className="h-1 w-16 bg-gradient-to-r from-primary-orange to-primary-blue mx-auto mb-6"></div>
          </div>
        )}
        
        <div className="max-w-6xl mx-auto px-4">
          <Slider {...sliderSettings}>
            {sponsors.map((sponsor) => (
              <SponsorCard key={sponsor.id} sponsor={sponsor} />
            ))}
          </Slider>
        </div>
      </div>
    )
  }

  return (
    <section className="py-16 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage: "url(/patterns/AMC_Blue Pattern-8.png)",
          backgroundSize: "200px",
          backgroundRepeat: "repeat",
          backgroundPosition: "center",
        }}
      />

      <div className="container mx-auto px-4 relative z-10">
        {showTitle && (
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-navy mb-4">
              Our <span className="text-primary-green">Sponsors</span>
            </h2>
            <div className="h-1 w-24 bg-gradient-to-r from-primary-orange to-primary-blue mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We are proud to partner with leading organizations that support our vision for Africa's maritime future
            </p>
          </div>
        )}

        {/* Sponsors Carousel */}
        <div className="relative max-w-6xl mx-auto mb-12 px-12">
          <Slider ref={sliderRef} {...sliderSettings} className="relative">
            {sponsors.map((sponsor) => (
              <div key={sponsor.id} className="px-2">
                <SponsorCard sponsor={sponsor} />
              </div>
            ))}
          </Slider>
          
          {/* Custom Navigation Buttons */}
          {!isMobile && (
            <>
              <CustomPrevArrow onClick={() => sliderRef.current.slickPrev()} />
              <CustomNextArrow onClick={() => sliderRef.current.slickNext()} />
            </>
          )}
        </div>
      </div>
    </section>
  )
}

export default Sponsors